import { useMemo } from 'react'

import { CreatorPermissionsApiService, type TeamMemberDto } from '@api'
import { useQuery } from 'react-query'

import { LocalStorageKeys } from '@/consts/localStorage.ts'
import { teamService } from '@/modules/team/service/team-service.ts'
import { handleError } from '@/utils/error'

export const useCreatorTeamAccess = (creatorId: number) => {
  const shouldShowAllPermissions = localStorage.getItem(
    LocalStorageKeys.SHOW_ALL_PERMISSIONS,
  )

  const { data: creatorAccess, isFetching: isCreatorAccessesLoading } =
    useQuery(
      ['creatorAccess', creatorId],
      () =>
        CreatorPermissionsApiService.getAllPermissions({
          creatorId,
        }),
      { enabled: !!creatorId },
    )

  const {
    data: availablePermissions,
    isFetching: isAvailablePermissionsLoading,
  } = useQuery(
    ['creatorsAvailablePermissions', creatorId],
    teamService.getAvailableCreatorPermissions,
    {
      cacheTime: 0,
      enabled: !!creatorId,
      onError: (error) =>
        handleError(error, 'Failed to get available creator permissions'),
    },
  )

  const membersMap = useMemo(
    () =>
      (creatorAccess?.members ?? []).reduce(
        (acc, { member }) => {
          acc[member!.id] = member!

          return acc
        },
        {} as Record<TeamMemberDto['id'], TeamMemberDto>,
      ),
    [creatorAccess],
  )

  const filteredAvailablePermissions = availablePermissions?.filter(
    (permission) => shouldShowAllPermissions || permission.display,
  )

  return {
    membersMap,
    creatorAccess,
    availablePermissions: filteredAvailablePermissions,
    isFetching: isCreatorAccessesLoading || isAvailablePermissionsLoading,
  }
}
